import { VbcaBenchmark, StrataData } from './mips-data-service';
import { lookupDecile, calculatePointsFromDecile } from './scoring-core';

export type PracticeSize = 'small' | 'large';

export interface MeasureInputs {
  denominator: number;
  performanceMet: number;
  performanceNotMet: number;
  numeratorExceptions: number;
  practiceSize: PracticeSize;
  benchmark: VbcaBenchmark | null;
}

export interface ScoringResult {
  score: number;
  appliedRule: string;
  decile: number | null;
  details?: string;
  passesCompleteness: boolean;
  meetsCaseMinimum: boolean;
  dataCompletenessRate: number;
  performanceRate: number;
}

/**
 * Calculate Data Completeness Rate
 * Formula: (Performance Met + Performance Not Met + Numerator Exceptions) / Denominator × 100
 */
export function computeDataCompletenessRate(
  denominator: number | string,
  met: number | string,
  notMet: number | string,
  exceptions: number | string
): number {
  const numDenominator = Number(denominator);
  const numMet = Number(met);
  const numNotMet = Number(notMet);
  const numExceptions = Number(exceptions);

  if (numDenominator === 0) return 0;
  return ((numMet + numNotMet + numExceptions) / numDenominator) * 100;
}

/**
 * Calculate Performance Rate (CMS formula)
 * Formula: Performance Met / (Data Completeness Numerator - Denominator Exceptions)
 * Which simplifies to: Performance Met / (Performance Met + Performance Not Met)
 * Exceptions are excluded from the PR denominator per CMS MIPS methodology.
 */
export function computePerformanceRate(
  met: number | string,
  notMet: number | string,
  exceptions: number | string
): number {
  const numMet = Number(met);
  const numNotMet = Number(notMet);

  const prDenominator = numMet + numNotMet;
  if (prDenominator === 0) return 0;
  return (numMet / prDenominator) * 100;
}

// Decile mathematics (parseDecileRange, findDecile, calculatePointsFromDecile,
// applyCapAndFloor) extracted VERBATIM to ./scoring-core.ts (P1, 2026-07) —
// shared with the APP Plus engine. Semantics unchanged; regression battery:
// __tests__/mips-scoring.regression.test.ts.

/**
 * Main scoring function that implements MIPS quality measure scoring logic
 * 
 * Key Rules:
 * - Regular measures: require 20+ cases
 * - Bonus measures (with scoring floor): require only 1+ case
 * - Scoring floors guarantee minimum points (5 or 7) BUT only if:
 *   1. Data completeness ≥ 75% (mandatory)
 *   2. Case minimum met (1+ for bonus, 20+ for regular)
 */
export function scoreQualityMeasure(inputs: MeasureInputs & {
  ignoreCompleteness?: boolean;
  ignoreCaseMinimum?: boolean;
  /** Pre-computed PR for split-algorithm strata measures (PR/DC come from different strata) */
  overridePerformanceRate?: number;
  /** Pre-computed DC for split-algorithm strata measures (PR/DC come from different strata) */
  overrideDataCompletenessRate?: number;
}): ScoringResult {
  const {
    denominator,
    performanceMet,
    performanceNotMet,
    numeratorExceptions,
    practiceSize,
    benchmark,
    ignoreCompleteness = false,
    ignoreCaseMinimum = false,
    overridePerformanceRate,
    overrideDataCompletenessRate
  } = inputs;
  
  // Debug logging for zero denominator issue
  if (denominator === 0 && benchmark) {
    console.log('[SCORING DEBUG] Measure with 0 denominator - input values:', {
      denominator,
      practiceSize,
      hasBenchmark: !!benchmark,
      scoringFloor: benchmark?.scoringFloor,
      ignoreCaseMinimum,
      ignoreCompleteness
    });
  }

  // Calculate metrics – use pre-computed overrides for split-algorithm strata measures
  // where PR and DC come from different strata and cannot be derived from a single set of counts.
  const dataCompletenessRate = overrideDataCompletenessRate != null
    ? overrideDataCompletenessRate
    : computeDataCompletenessRate(denominator, performanceMet, performanceNotMet, numeratorExceptions);
  
  const performanceRate = overridePerformanceRate != null
    ? overridePerformanceRate
    : computePerformanceRate(performanceMet, performanceNotMet, numeratorExceptions);

  // No benchmark data - apply appropriate baseline scoring
  if (!benchmark) {
    console.warn('No benchmark data found for measure scoring', inputs);
    
    // Check denominator eligibility
    if (denominator === 0) {
      return {
        score: 0,
        appliedRule: 'No benchmark available - No denominator-eligible encounters',
        decile: null,
        passesCompleteness: false,
        meetsCaseMinimum: false,
        dataCompletenessRate,
        performanceRate
      };
    }
    
    // For measures without benchmark:
    // - Small practices get 3 points (if denominator >= 1)
    // - Large practices get 0 points
    // Note: We can't determine scoring floor without benchmark data,
    // so we'll need to handle that separately if needed
    const score = practiceSize === 'small' ? 3 : 0;
    const appliedRule = `No benchmark available - ${practiceSize} practice baseline (denominator: ${denominator})`;
    
    return {
      score,
      appliedRule,
      decile: null,
      passesCompleteness: dataCompletenessRate >= 75,
      meetsCaseMinimum: denominator >= 1, // At least 1 for no-benchmark
      dataCompletenessRate,
      performanceRate
    };
  }

  // Parse benchmark properties
  const isInverse = benchmark.isInverse === 'TRUE';
  const hasSevenPointCap = benchmark.sevenPointCap === 'Yes';
  const hasScoringFloor = benchmark.scoringFloor === '5_Points' || benchmark.scoringFloor === '7_Points';

  // Check thresholds
  const passesCompleteness = dataCompletenessRate >= 75;
  const totalComplete = performanceMet + performanceNotMet + numeratorExceptions;
  
  // Bonus measures (with scoring floor) only need 1 case minimum, regular measures need 20
  const requiredCaseMinimum = hasScoringFloor ? 1 : 20;
  const meetsCaseMinimum = denominator >= requiredCaseMinimum;

  // Check if measure is topped out
  if (benchmark.toppedOut === 'Yes') {
    // For topped out measures, you typically can't score higher than a certain decile
    // This would need specific business rules
  }

  // Apply scoring rules in order of precedence

  // Rule 1: Fail data completeness (skip if ignoreCompleteness simulation is active)
  if (!ignoreCompleteness && !passesCompleteness) {
    // Special case: ANY measure with 0 denominator gets 0 points
    if (denominator === 0) {
      console.log('[SCORING DEBUG] In Rule 1 - Zero denominator detected, returning 0 points');
      return {
        score: 0,
        appliedRule: `No denominator-eligible encounters`,
        decile: null,
        details: `Denominator: ${denominator} (at least 1 required for scoring)`,
        passesCompleteness,
        meetsCaseMinimum: false,
        dataCompletenessRate,
        performanceRate
      };
    }
    
    // Bonus measures: 0 points for both small and large practices when failing completeness
    // Non-bonus measures: Small practices get 3 points, large practices get 0 points
    const baseScore = hasScoringFloor ? 0 : (practiceSize === 'small' ? 3 : 0);
    
    return {
      score: baseScore,
      appliedRule: `Failed 75% data completeness requirement (${practiceSize} practice)${hasScoringFloor ? ' - bonus measure gets 0 points' : ''}`,
      decile: null,
      details: `Data completeness: ${dataCompletenessRate.toFixed(1)}%${hasScoringFloor ? ' (bonus measures require 75%+)' : ''}`,
      passesCompleteness,
      meetsCaseMinimum,
      dataCompletenessRate,
      performanceRate
    };
  }

  // Debug before Rule 2
  if (denominator === 0) {
    console.log('[SCORING DEBUG] Before Rule 2 check:', {
      ignoreCaseMinimum,
      meetsCaseMinimum,
      willEnterBlock: !ignoreCaseMinimum && !meetsCaseMinimum
    });
  }
  
  // Rule 2: Fail case minimum (skip if ignoreCaseMinimum simulation is active)
  if (!ignoreCaseMinimum && !meetsCaseMinimum) {
    // Note: Zero denominator is already handled in Rule 1
    console.log('[SCORING DEBUG] Case minimum failed:', {
      denominator,
      practiceSize,
      requiredCaseMinimum,
      hasScoringFloor
    });
    
    // Small practices get 3 points (if denominator > 0), large practices get 0 points
    const score = practiceSize === 'small' ? 3 : 0;
    
    // Unless has scoring floor, then use floor
    if (hasScoringFloor) {
      const floorPoints = benchmark.scoringFloor === '7_Points' ? 7 : 
                         benchmark.scoringFloor === '5_Points' ? 5 : 0;
      return {
        score: Math.max(score, floorPoints),
        appliedRule: `Failed ${requiredCaseMinimum}-case minimum (${practiceSize} practice) with ${benchmark.scoringFloor || 'no'} floor`,
        decile: null,
        details: `Denominator: ${denominator} (required: ${requiredCaseMinimum})`,
        passesCompleteness,
        meetsCaseMinimum,
        dataCompletenessRate,
        performanceRate
      };
    }
    
    return {
      score,
      appliedRule: `Failed ${requiredCaseMinimum}-case minimum (${practiceSize} practice)`,
      decile: null,
      details: `Total cases: ${totalComplete} (required: ${requiredCaseMinimum})`,
      passesCompleteness,
      meetsCaseMinimum,
      dataCompletenessRate,
      performanceRate
    };
  }

  // Rule 3: Calculate decile-based score. The lookup rounds to 2dp so a rate in a
  // 0.01 gap between published bands doesn't fall through to 0; partial credit
  // below still uses the unrounded rate (identical for every non-gap rate).
  const { decile } = lookupDecile(performanceRate, benchmark, isInverse);
  
  if (decile === null) {
    // For bonus measures with no decile data, apply scoring floor if they have one
    if (hasScoringFloor) {
      const floorPoints = benchmark.scoringFloor === '7_Points' ? 7 : 
                         benchmark.scoringFloor === '5_Points' ? 5 : 0;
      return {
        score: floorPoints,
        appliedRule: `No decile data available - applying ${benchmark.scoringFloor} scoring floor`,
        decile: null,
        details: `Performance rate: ${performanceRate.toFixed(1)}% (no benchmark deciles)`,
        passesCompleteness,
        meetsCaseMinimum,
        dataCompletenessRate,
        performanceRate
      };
    }
    
    console.warn('Performance rate does not match any decile range', {
      performanceRate,
      benchmark,
      isInverse
    });
    return {
      score: 0,
      appliedRule: 'Performance rate does not match any decile range',
      decile: null,
      details: `Performance rate: ${performanceRate.toFixed(1)}%`,
      passesCompleteness,
      meetsCaseMinimum,
      dataCompletenessRate,
      performanceRate
    };
  }

  const points = calculatePointsFromDecile(
    decile,
    performanceRate,
    benchmark,
    isInverse,
    hasScoringFloor,
    benchmark.scoringFloor,
    hasSevenPointCap
  );

  let ruleDescription = `Decile ${decile} scoring`;
  if (isInverse) ruleDescription += ' (inverse)';
  if (hasSevenPointCap && decile > 7) ruleDescription += ' with 7-point cap';
  if (hasScoringFloor) ruleDescription += ` with ${benchmark.scoringFloor} floor`;

  return {
    score: points,
    appliedRule: ruleDescription,
    decile,
    details: `Performance rate: ${performanceRate.toFixed(1)}%`,
    passesCompleteness,
    meetsCaseMinimum,
    dataCompletenessRate,
    performanceRate
  };
}

/**
 * Calculate overall performance rate for multi-strata measures
 */
export function calculateOverallPerformanceRate(
  strataData: StrataData[],
  method: string,
  strataDefinitions?: Array<{name?: string; description: string}>
): number {
  if (strataData.length === 0) return 0;

  switch (method) {
    case 'Simple Average':
      return calculateSimpleAveragePerformanceRate(strataData);
    
    case 'Weighted Average':
      return calculateWeightedAveragePerformanceRate(strataData);
    
    case '1st Performance Rate':
      return getNthStrataPerformanceRate(strataData, 1, strataDefinitions);
    
    case '2nd Performance Rate':
      return getNthStrataPerformanceRate(strataData, 2, strataDefinitions);
    
    case '3rd Performance Rate':
      return getNthStrataPerformanceRate(strataData, 3, strataDefinitions);
    
    case '4th Performance Rate':
      return getNthStrataPerformanceRate(strataData, 4, strataDefinitions);
    
    case '5th Performance Rate':
      return getNthStrataPerformanceRate(strataData, 5, strataDefinitions);
    
    default:
      console.warn(`Unknown performance rate calculation method: ${method}`);
      return calculateSimpleAveragePerformanceRate(strataData);
  }
}

/**
 * Calculate overall data completeness rate for multi-strata measures
 */
export function calculateOverallDataCompletenessRate(
  strataData: StrataData[],
  method: string,
  strataDefinitions?: Array<{name?: string; description: string}>
): number {
  if (strataData.length === 0) return 0;

  switch (method) {
    case 'Simple Average':
      return calculateSimpleAverageDataCompletenessRate(strataData);
    
    case 'Weighted Average':
      return calculateWeightedAverageDataCompletenessRate(strataData);
    
    case '1st Performance Rate':
      return getNthStrataDataCompletenessRate(strataData, 1, strataDefinitions);
    
    case '2nd Performance Rate':
      return getNthStrataDataCompletenessRate(strataData, 2, strataDefinitions);
    
    case '3rd Performance Rate':
      return getNthStrataDataCompletenessRate(strataData, 3, strataDefinitions);
    
    case '4th Performance Rate':
      return getNthStrataDataCompletenessRate(strataData, 4, strataDefinitions);
    
    case '5th Performance Rate':
      return getNthStrataDataCompletenessRate(strataData, 5, strataDefinitions);
    
    default:
      console.warn(`Unknown data completeness calculation method: ${method}`);
      return calculateSimpleAverageDataCompletenessRate(strataData);
  }
}

/**
 * Calculate simple average performance rate
 * Formula: Sum of all performance rates / Number of strata
 */
function calculateSimpleAveragePerformanceRate(strataData: StrataData[]): number {
  if (strataData.length === 0) return 0;
  
  const totalPerformanceRate = strataData.reduce((sum, strata) => {
    const pr = computePerformanceRate(strata.met, strata.not, strata.exc);
    return sum + pr;
  }, 0);
  
  return totalPerformanceRate / strataData.length;
}

/**
 * Calculate weighted average performance rate (CMS formula)
 * Formula: Sum of all Met / Sum of all (Met + Not Met)
 * Exceptions are excluded from the PR denominator per CMS methodology.
 */
function calculateWeightedAveragePerformanceRate(strataData: StrataData[]): number {
  const totalNumerator = strataData.reduce((sum, strata) => sum + Number(strata.met), 0);
  const totalDenominator = strataData.reduce((sum, strata) => sum + Number(strata.met) + Number(strata.not), 0);
  
  if (totalDenominator === 0) return 0;
  return (totalNumerator / totalDenominator) * 100;
}

/**
 * Get performance rate from specific stratum by order
 */
function getNthStrataPerformanceRate(
  strataData: StrataData[],
  position: number,
  strataDefinitions?: Array<{name?: string; description: string}>
): number {
  // If we have strata definitions from CMS, use that order
  if (strataDefinitions && strataDefinitions.length >= position) {
    const targetDefinition = strataDefinitions[position - 1];
    const targetStrata = strataData.find(s => 
      s.name === targetDefinition.name || 
      s.description === targetDefinition.description
    );
    
    if (targetStrata) {
      return computePerformanceRate(targetStrata.met, targetStrata.not, targetStrata.exc);
    }
  }
  
  // Fallback to array order
  if (strataData.length >= position) {
    const targetStrata = strataData[position - 1];
    return computePerformanceRate(targetStrata.met, targetStrata.not, targetStrata.exc);
  }
  
  console.warn(`Cannot find ${position}${getOrdinalSuffix(position)} stratum in data`);
  return 0;
}

/**
 * Calculate simple average data completeness rate
 */
function calculateSimpleAverageDataCompletenessRate(strataData: StrataData[]): number {
  if (strataData.length === 0) return 0;
  
  const totalCompletenessRate = strataData.reduce((sum, strata) => {
    const dc = computeDataCompletenessRate(strata.den, strata.met, strata.not, strata.exc);
    return sum + dc;
  }, 0);
  
  return totalCompletenessRate / strataData.length;
}

/**
 * Calculate weighted average data completeness rate
 */
function calculateWeightedAverageDataCompletenessRate(strataData: StrataData[]): number {
  const totalNumerator = strataData.reduce((sum, strata) => sum + Number(strata.met) + Number(strata.not) + Number(strata.exc), 0);
  const totalDenominator = strataData.reduce((sum, strata) => sum + Number(strata.den), 0);
  
  if (totalDenominator === 0) return 0;
  return (totalNumerator / totalDenominator) * 100;
}

/**
 * Get data completeness rate from specific stratum by order
 */
function getNthStrataDataCompletenessRate(
  strataData: StrataData[],
  position: number,
  strataDefinitions?: Array<{name?: string; description: string}>
): number {
  // If we have strata definitions from CMS, use that order
  if (strataDefinitions && strataDefinitions.length >= position) {
    const targetDefinition = strataDefinitions[position - 1];
    const targetStrata = strataData.find(s => 
      s.name === targetDefinition.name || 
      s.description === targetDefinition.description
    );
    
    if (targetStrata) {
      return computeDataCompletenessRate(targetStrata.den, targetStrata.met, targetStrata.not, targetStrata.exc);
    }
  }
  
  // Fallback to array order
  if (strataData.length >= position) {
    const targetStrata = strataData[position - 1];
    return computeDataCompletenessRate(targetStrata.den, targetStrata.met, targetStrata.not, targetStrata.exc);
  }
  
  console.warn(`Cannot find ${position}${getOrdinalSuffix(position)} stratum in data`);
  return 0;
}

/**
 * Helper function to get ordinal suffix (1st, 2nd, 3rd, etc.)
 */
function getOrdinalSuffix(num: number): string {
  const lastDigit = num % 10;
  const lastTwoDigits = num % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 13) {
    return 'th';
  }
  
  switch (lastDigit) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}
